<script setup>
import { onMounted, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'dashboard/components-next/button/Button.vue';
import ResellerApplicationAPI from 'dashboard/api/resellerApplication';

const { t } = useI18n();
const router = useRouter();
const { accountScopedUrl } = useAccount();

const isLoading = ref(true);
const isSubmitting = ref(false);
const application = ref(null);
const isPartner = ref(false);

const partnerType = ref('reseller');
const message = ref('');

const formSectionRef = ref(null);
const howSectionRef = ref(null);

const partnerTypes = computed(() => [
  {
    value: 'reseller',
    label: t('REVENDA.APPLY.TYPES.RESELLER.LABEL'),
    description: t('REVENDA.APPLY.TYPES.RESELLER.DESC'),
    icon: 'i-lucide-store',
  },
  {
    value: 'white_label',
    label: t('REVENDA.APPLY.TYPES.WHITE_LABEL.LABEL'),
    description: t('REVENDA.APPLY.TYPES.WHITE_LABEL.DESC'),
    icon: 'i-lucide-palette',
  },
]);

const benefitCards = computed(() => [
  {
    icon: 'i-lucide-palette',
    title: t('REVENDA.APPLY.BENEFITS.CARDS.BRAND.TITLE'),
    description: t('REVENDA.APPLY.BENEFITS.CARDS.BRAND.DESC'),
  },
  {
    icon: 'i-lucide-repeat',
    title: t('REVENDA.APPLY.BENEFITS.CARDS.RECURRING.TITLE'),
    description: t('REVENDA.APPLY.BENEFITS.CARDS.RECURRING.DESC'),
  },
  {
    icon: 'i-lucide-tag',
    title: t('REVENDA.APPLY.BENEFITS.CARDS.PRICING.TITLE'),
    description: t('REVENDA.APPLY.BENEFITS.CARDS.PRICING.DESC'),
  },
  {
    icon: 'i-lucide-layout-dashboard',
    title: t('REVENDA.APPLY.BENEFITS.CARDS.PANEL.TITLE'),
    description: t('REVENDA.APPLY.BENEFITS.CARDS.PANEL.DESC'),
  },
  {
    icon: 'i-lucide-life-buoy',
    title: t('REVENDA.APPLY.BENEFITS.CARDS.SUPPORT.TITLE'),
    description: t('REVENDA.APPLY.BENEFITS.CARDS.SUPPORT.DESC'),
  },
  {
    icon: 'i-lucide-server-off',
    title: t('REVENDA.APPLY.BENEFITS.CARDS.INFRA.TITLE'),
    description: t('REVENDA.APPLY.BENEFITS.CARDS.INFRA.DESC'),
  },
]);

const howSteps = computed(() => [
  {
    title: t('REVENDA.APPLY.HOW.STEPS.APPLY.TITLE'),
    description: t('REVENDA.APPLY.HOW.STEPS.APPLY.DESC'),
  },
  {
    title: t('REVENDA.APPLY.HOW.STEPS.APPROVED.TITLE'),
    description: t('REVENDA.APPLY.HOW.STEPS.APPROVED.DESC'),
  },
  {
    title: t('REVENDA.APPLY.HOW.STEPS.CONFIGURE.TITLE'),
    description: t('REVENDA.APPLY.HOW.STEPS.CONFIGURE.DESC'),
  },
  {
    title: t('REVENDA.APPLY.HOW.STEPS.SELL.TITLE'),
    description: t('REVENDA.APPLY.HOW.STEPS.SELL.DESC'),
  },
]);

const marginPoints = computed(() => [
  t('REVENDA.APPLY.MARGIN.POINT_1'),
  t('REVENDA.APPLY.MARGIN.POINT_2'),
  t('REVENDA.APPLY.MARGIN.POINT_3'),
]);

const status = computed(() => application.value?.status);
const isPending = computed(() => status.value === 'pending');
const isRejected = computed(() => status.value === 'rejected');

const fetchApplication = async () => {
  isLoading.value = true;
  try {
    const { data } = await ResellerApplicationAPI.get();
    application.value = data.application;
    isPartner.value = data.is_partner;
  } catch (error) {
    application.value = null;
  } finally {
    isLoading.value = false;
  }
};

const submit = async () => {
  isSubmitting.value = true;
  try {
    const { data } = await ResellerApplicationAPI.apply({
      partnerType: partnerType.value,
      message: message.value,
    });
    application.value = data.application;
    useAlert(t('REVENDA.APPLY.SUCCESS'));
  } catch (error) {
    useAlert(error?.response?.data?.error || t('REVENDA.APPLY.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

const goToPanel = () => {
  router.push(accountScopedUrl('revenda'));
};

const scrollToForm = () => {
  formSectionRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const scrollToHow = () => {
  howSectionRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

onMounted(fetchApplication);
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-auto">
    <div
      v-if="isLoading"
      class="flex items-center justify-center flex-1 text-n-slate-11"
    >
      <span class="i-lucide-loader-circle animate-spin text-2xl" />
    </div>

    <div
      v-else
      class="flex flex-col w-full max-w-5xl gap-10 p-4 mx-auto sm:p-6 lg:p-10"
    >
      <!-- Já é parceiro -->
      <div
        v-if="isPartner"
        class="flex flex-col items-center gap-4 p-10 text-center border rounded-2xl border-n-weak bg-n-solid-2"
      >
        <span
          class="flex items-center justify-center text-3xl rounded-2xl i-lucide-badge-check text-n-brand bg-n-brand/10 w-16 h-16"
        />
        <p class="text-lg font-medium text-n-slate-12">
          {{ t('REVENDA.APPLY.ALREADY_PARTNER.TITLE') }}
        </p>
        <p class="max-w-md text-sm text-n-slate-11">
          {{ t('REVENDA.APPLY.ALREADY_PARTNER.BODY') }}
        </p>
        <Button
          :label="t('REVENDA.APPLY.ALREADY_PARTNER.CTA')"
          icon="i-lucide-arrow-right"
          size="lg"
          trailing-icon
          @click="goToPanel"
        />
      </div>

      <!-- Solicitação pendente -->
      <div
        v-else-if="isPending"
        class="flex flex-col items-center gap-4 p-10 text-center border rounded-2xl border-n-amber-6 bg-n-amber-2"
      >
        <span
          class="flex items-center justify-center text-3xl rounded-2xl i-lucide-clock text-n-amber-10 bg-n-amber-3 w-16 h-16"
        />
        <p class="text-lg font-medium text-n-slate-12">
          {{ t('REVENDA.APPLY.PENDING.TITLE') }}
        </p>
        <p class="max-w-md text-sm text-n-slate-11">
          {{ t('REVENDA.APPLY.PENDING.BODY') }}
        </p>
      </div>

      <!-- Página de venda do programa de parceiros -->
      <template v-else>
        <!-- Hero -->
        <section
          class="relative flex flex-col items-center gap-5 px-6 py-14 overflow-hidden text-center border rounded-3xl border-n-weak bg-gradient-to-br from-n-brand/10 via-n-solid-2 to-n-solid-1 sm:px-10"
        >
          <span
            class="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full bg-n-brand/10 text-n-blue-text w-fit"
          >
            <span class="i-lucide-sparkles text-sm" />
            {{ t('REVENDA.APPLY.HERO.BADGE') }}
          </span>

          <h1
            class="max-w-2xl text-3xl font-semibold leading-tight text-n-slate-12 sm:text-4xl"
          >
            {{ t('REVENDA.APPLY.TITLE') }}
          </h1>

          <p class="max-w-xl text-base text-n-slate-11">
            {{ t('REVENDA.APPLY.SUBTITLE') }}
          </p>

          <div class="flex flex-col items-center gap-3 mt-2 sm:flex-row">
            <Button
              :label="t('REVENDA.APPLY.HERO.CTA_PRIMARY')"
              icon="i-lucide-rocket"
              size="lg"
              @click="scrollToForm"
            />
            <Button
              :label="t('REVENDA.APPLY.HERO.CTA_SECONDARY')"
              variant="ghost"
              color="slate"
              size="lg"
              icon="i-lucide-circle-play"
              @click="scrollToHow"
            />
          </div>
        </section>

        <!-- Benefícios -->
        <section class="flex flex-col gap-5">
          <div class="flex flex-col gap-1 text-center">
            <h2 class="text-xl font-medium text-n-slate-12">
              {{ t('REVENDA.APPLY.BENEFITS.TITLE') }}
            </h2>
            <p class="text-sm text-n-slate-11">
              {{ t('REVENDA.APPLY.BENEFITS.SUBTITLE') }}
            </p>
          </div>

          <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div
              v-for="card in benefitCards"
              :key="card.title"
              class="flex flex-col gap-3 p-5 border rounded-2xl border-n-weak bg-n-solid-2"
            >
              <span
                class="flex items-center justify-center w-10 h-10 text-lg rounded-xl bg-n-brand/10 text-n-brand"
                :class="card.icon"
              />
              <p class="text-sm font-medium text-n-slate-12">
                {{ card.title }}
              </p>
              <p class="text-sm text-n-slate-11">
                {{ card.description }}
              </p>
            </div>
          </div>
        </section>

        <!-- Como funciona -->
        <section ref="howSectionRef" class="flex flex-col gap-5 scroll-mt-6">
          <div class="flex flex-col gap-1 text-center">
            <h2 class="text-xl font-medium text-n-slate-12">
              {{ t('REVENDA.APPLY.HOW.TITLE') }}
            </h2>
            <p class="text-sm text-n-slate-11">
              {{ t('REVENDA.APPLY.HOW.SUBTITLE') }}
            </p>
          </div>

          <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div
              v-for="(step, index) in howSteps"
              :key="step.title"
              class="flex flex-col gap-2 p-5 border rounded-2xl border-n-weak bg-n-solid-2"
            >
              <span
                class="flex items-center justify-center w-8 h-8 text-sm font-semibold rounded-full bg-n-brand text-white tabular-nums"
              >
                {{ index + 1 }}
              </span>
              <p class="text-sm font-medium text-n-slate-12">
                {{ step.title }}
              </p>
              <p class="text-sm text-n-slate-11">
                {{ step.description }}
              </p>
            </div>
          </div>
        </section>

        <!-- Destaque de margem -->
        <section
          class="flex flex-col gap-4 p-8 overflow-hidden border rounded-3xl border-n-brand/20 bg-gradient-to-br from-n-brand/10 to-n-solid-2 sm:p-10"
        >
          <span
            class="flex items-center justify-center text-2xl rounded-2xl i-lucide-trending-up text-n-brand bg-n-brand/10 w-12 h-12"
          />
          <h2 class="text-xl font-semibold text-n-slate-12 sm:text-2xl">
            {{ t('REVENDA.APPLY.MARGIN.TITLE') }}
          </h2>
          <p class="max-w-2xl text-sm text-n-slate-11 sm:text-base">
            {{ t('REVENDA.APPLY.MARGIN.BODY') }}
          </p>
          <div class="flex flex-wrap gap-3 mt-1">
            <span
              v-for="point in marginPoints"
              :key="point"
              class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border rounded-full border-n-brand/20 bg-n-solid-1 text-n-slate-12"
            >
              <span class="text-base i-lucide-check text-n-brand" />
              {{ point }}
            </span>
          </div>
        </section>

        <!-- Formulário / CTA final -->
        <section
          ref="formSectionRef"
          class="grid gap-6 p-6 border lg:grid-cols-2 rounded-3xl border-n-weak bg-n-solid-2 scroll-mt-6 sm:p-8"
        >
          <div class="flex flex-col gap-1">
            <h2 class="text-xl font-medium text-n-slate-12">
              {{ t('REVENDA.APPLY.FORM.TITLE') }}
            </h2>
            <p class="text-sm text-n-slate-11">
              {{ t('REVENDA.APPLY.FORM.REASSURANCE') }}
            </p>
          </div>

          <div class="flex flex-col gap-5">
            <div
              v-if="isRejected"
              class="flex items-start gap-2 p-3 text-sm border rounded-lg border-n-ruby-6 bg-n-ruby-2 text-n-slate-12"
            >
              <span
                class="i-lucide-circle-x text-n-ruby-10 text-base shrink-0"
              />
              {{ t('REVENDA.APPLY.REJECTED') }}
            </div>

            <div class="flex flex-col gap-3">
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('REVENDA.APPLY.TYPE_LABEL') }}
              </span>
              <button
                v-for="type in partnerTypes"
                :key="type.value"
                type="button"
                class="flex items-start gap-3 p-4 text-left transition-colors border rounded-xl"
                :class="
                  partnerType === type.value
                    ? 'border-n-brand bg-n-brand/5'
                    : 'border-n-weak hover:border-n-slate-6'
                "
                @click="partnerType = type.value"
              >
                <span
                  :class="type.icon"
                  class="text-xl text-n-slate-11 mt-0.5"
                />
                <span class="flex flex-col gap-0.5">
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ type.label }}
                  </span>
                  <span class="text-xs text-n-slate-11">{{
                    type.description
                  }}</span>
                </span>
                <span
                  class="ml-auto mt-1 text-lg shrink-0"
                  :class="
                    partnerType === type.value
                      ? 'i-lucide-circle-check-big text-n-brand'
                      : 'i-lucide-circle text-n-slate-6'
                  "
                />
              </button>
            </div>

            <label class="flex flex-col gap-1.5">
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('REVENDA.APPLY.MESSAGE_LABEL') }}
              </span>
              <textarea
                v-model="message"
                rows="4"
                :placeholder="t('REVENDA.APPLY.MESSAGE_PLACEHOLDER')"
                class="w-full px-3 py-2 text-sm border rounded-lg resize-none border-n-weak bg-n-solid-1 text-n-slate-12 focus:border-n-brand focus:outline-none"
              />
            </label>

            <Button
              :label="t('REVENDA.APPLY.SUBMIT')"
              :is-loading="isSubmitting"
              icon="i-lucide-send"
              size="lg"
              class="self-start"
              @click="submit"
            />
          </div>
        </section>
      </template>
    </div>
  </div>
</template>
